/**
 * DocContent Component (AC-653)
 *
 * A component that renders markdown documentation content with support for
 * GitHub Flavored Markdown, syntax highlighting, and internal/external link handling.
 *
 * Technical Design Specification:
 * - Uses react-markdown for rendering markdown content
 * - Supports GitHub Flavored Markdown (tables, strikethrough, task lists) via remark-gfm
 * - Syntax highlighting for code blocks using rehype-highlight
 * - Shows loading skeleton when isLoading is true
 * - Shows empty state when content is null or empty
 * - Internal documentation links (starting with docs/) trigger onLinkClick
 * - External links open in new tab with rel="noopener noreferrer"
 *
 * @see AC-653 - Create DocContent component with Markdown rendering
 * @see AC-648 - Epic: Contextual Help System
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';

export interface DocContentProps {
  /** Markdown content to render, or null for empty state */
  content: string | null;
  /** Whether content is loading (shows skeleton) */
  isLoading?: boolean;
  /** Error message to display if document failed to load */
  error?: string | null;
  /** Callback when an internal documentation link is clicked */
  onLinkClick?: (href: string) => void;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * Loading skeleton component for the DocContent.
 */
const LoadingSkeleton: React.FC = () => (
  <div data-testid="doc-content-loading" role="status" className="animate-pulse space-y-4">
    {/* Title skeleton */}
    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
    {/* Paragraph skeletons */}
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5"></div>
    </div>
    {/* Section skeleton */}
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mt-6"></div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
    </div>
    <span className="sr-only">Loading documentation...</span>
  </div>
);

/**
 * Empty state component shown when no content is available.
 */
const EmptyState: React.FC = () => (
  <div
    data-testid="doc-content-empty"
    className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400"
  >
    <svg
      className="w-12 h-12 mb-4 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
    <p className="text-lg font-medium">No content selected</p>
    <p className="text-sm">Select a topic from the navigation to view documentation.</p>
  </div>
);

/**
 * Error state component shown when document fails to load.
 */
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div
    data-testid="doc-content-error"
    role="alert"
    className="flex flex-col items-center justify-center py-12 text-red-500 dark:text-red-400"
  >
    <svg
      className="w-12 h-12 mb-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
    <p className="text-lg font-medium">Failed to load document</p>
    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{message}</p>
  </div>
);

/**
 * Check if a href is an internal documentation link.
 * Internal links start with "docs/" (relative documentation paths).
 */
const isInternalDocLink = (href: string): boolean => {
  return href.startsWith('docs/');
};

/**
 * DocContent - Renders markdown documentation with GFM support and syntax highlighting.
 *
 * @param content - Markdown content to render (null shows empty state)
 * @param isLoading - Show loading skeleton when true
 * @param onLinkClick - Callback for internal documentation link clicks
 */
const DocContent: React.FC<DocContentProps> = ({
  content,
  isLoading = false,
  error = null,
  onLinkClick,
  className = '',
}) => {
  // Show loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error) {
    return <ErrorState message={error} />;
  }

  // Show empty state for null, empty, or whitespace-only content
  if (!content || content.trim() === '') {
    return <EmptyState />;
  }

  /**
   * Custom link renderer that handles internal vs external links.
   * - Internal links (starting with "docs/") trigger onLinkClick
   * - External links open in new tab with security attributes
   */
  const CustomLink: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }> = ({
    href,
    children,
    ...props
  }) => {
    if (!href) {
      return <a {...props}>{children}</a>;
    }

    if (isInternalDocLink(href)) {
      // Internal documentation link
      return (
        <a
          href={href}
          onClick={(e) => {
            e.preventDefault();
            if (onLinkClick) {
              onLinkClick(href);
            }
          }}
          className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 hover:underline"
          {...props}
        >
          {children}
        </a>
      );
    }

    // External link - open in new tab with security attributes
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 hover:underline"
        {...props}
      >
        {children}
      </a>
    );
  };

  return (
    <div className={`doc-content prose prose-sm prose-slate dark:prose-invert max-w-none ${className}`.trim()}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          // Custom link handling
          a: CustomLink,
          // Ensure code blocks have proper role for accessibility
          code: ({ className, children, ...props }) => {
            // Check if this is an inline code or code block
            const isInline = !className;
            if (isInline) {
              return (
                <code
                  className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800 dark:text-gray-200"
                  {...props}
                >
                  {children}
                </code>
              );
            }
            // Code block - add role="code" for accessibility testing
            return (
              <code role="code" className={`${className || ''}`} {...props}>
                {children}
              </code>
            );
          },
          // Style pre blocks for code
          pre: ({ children, ...props }) => (
            <pre
              className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-x-auto text-sm"
              {...props}
            >
              {children}
            </pre>
          ),
          // Ensure tables have proper styling
          table: ({ children, ...props }) => (
            <table
              className="min-w-full divide-y divide-gray-300 border border-gray-300 dark:border-gray-600"
              {...props}
            >
              {children}
            </table>
          ),
          thead: ({ children, ...props }) => (
            <thead className="bg-gray-50 dark:bg-gray-900" {...props}>
              {children}
            </thead>
          ),
          th: ({ children, ...props }) => (
            <th
              scope="col"
              className="px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-warm-white border border-gray-300 dark:border-gray-600"
              {...props}
            >
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600"
              {...props}
            >
              {children}
            </td>
          ),
          // Blockquote styling
          blockquote: ({ children, ...props }) => (
            <blockquote
              className="border-l-4 border-primary-500 pl-4 py-2 my-4 bg-primary-50 dark:bg-primary-900/20 text-gray-700 dark:text-gray-300 italic"
              {...props}
            >
              {children}
            </blockquote>
          ),
          // Images with proper styling
          img: ({ src, alt, ...props }) => (
            <img
              src={src}
              alt={alt || ''}
              className="max-w-full h-auto rounded-lg shadow-sm"
              {...props}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default DocContent;
